import { Button } from '@neo/primitives/button'
import { Input } from '@neo/primitives/input'
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from '@neo/primitives/popover'
import { Skeleton } from '@neo/primitives/skeleton'
import { Typography } from '@neo/primitives/typography'
import { Search, X } from 'lucide-react'
import { useState } from 'react'
import { QueryError } from 'v2/api-console/shared/components/query-error'
import { useGetMcpToolsForOrgAppUuidQuery } from 'v2/pages/flow/queries'
import type { RActionDetails } from 'v2/types/api/settings'

const humanizeActionName = (actionName: string): string => {
  return actionName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export interface ActionsPopoverProps {
  appName: string
  appProvider: string
  orgAppUuid: string
  directActions: RActionDetails[]
  children: React.ReactNode
}

export const ActionsPopover = ({
  appName,
  appProvider,
  orgAppUuid,
  directActions,
  children,
}: ActionsPopoverProps) => {
  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [isRetrying, setIsRetrying] = useState(false)

  const shouldFetchMcpTools = directActions.length === 0
  const {
    data: mcpTools,
    isLoading,
    isError,
    refetch,
  } = useGetMcpToolsForOrgAppUuidQuery(appProvider, orgAppUuid, shouldFetchMcpTools && isOpen)

  const mcpActions =
    shouldFetchMcpTools && mcpTools
      ? mcpTools.actions?.map((action) => ({
          action_name: action.name,
          description: action.description,
        })) || []
      : []

  const actionsToShow = directActions.length > 0 ? directActions : mcpActions
  const showError = shouldFetchMcpTools && (isError || isRetrying)
  const filteredActions = actionsToShow.filter((action) =>
    action.action_name.toLowerCase().includes(search.toLowerCase()),
  )

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className="w-[500px] max-h-96 overflow-hidden"
        align="end"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col gap-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <Typography variant="H4/500">{appName} Actions</Typography>
            </div>
            <PopoverClose asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                data-testid="close-actions-popover"
              >
                <X size={20} />
                <span className="sr-only">Close</span>
              </Button>
            </PopoverClose>
          </div>
          <Input
            placeholder="Search"
            className="w-full"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            startIcon={Search}
          />
        </div>
        <div className="overflow-y-auto max-h-64 gap-3 flex flex-col">
          {showError && (
            <QueryError
              title="Couldn't load actions for this integration."
              testId="button-retry-integration-actions"
              isRetrying={isRetrying}
              onRetry={async () => {
                setIsRetrying(true)
                try {
                  await refetch()
                } finally {
                  setIsRetrying(false)
                }
              }}
            />
          )}
          {isLoading &&
            Array.from({ length: 3 }, (_, index) => (
              <div key={index} className="space-y-1">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-48" />
              </div>
            ))}
          {!isLoading && !showError && filteredActions.length === 0 && (
            <div className="text-center py-4">
              <Typography variant="S/400" className="text-secondary">
                No actions available for this integration
              </Typography>
            </div>
          )}
          {!isLoading &&
            filteredActions.length > 0 &&
            filteredActions.map((action) => (
              <div key={action.action_name} className="space-y-1">
                <Typography variant="S/500">{humanizeActionName(action.action_name)}</Typography>
                {action.description && (
                  <Typography variant="XS/400" className="text-tertiary">
                    {action.description}
                  </Typography>
                )}
              </div>
            ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
